import React, { useState, useEffect } from 'react';
import Modal from '../UI/Modal';
import ShareStateService from '../../services/ShareStateService';

interface SubmitStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategy: {
    name: string;
    difficulty: string;
    cost: string;
    description: string;
  };
  pinnedContentTypes: string[];
  webhookUrl: string;
  onSubmitSuccess: (message: string) => void;
}

interface IdolItem {
  id: string;
  name: string;
  stats: {
    contentType: string;
    modId: number;
    value: number;
    explicitType: string;
  }[];
  // Other properties not relevant for our use case
  [key: string]: unknown;
}

const SubmitStrategyModal: React.FC<SubmitStrategyModalProps> = ({
  isOpen,
  onClose,
  strategy,
  pinnedContentTypes,
  webhookUrl,
  onSubmitSuccess
}) => {
  const [formData, setFormData] = useState({
    shortDescription: '',
    authorLink: '',
    strategyLink: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [filteredContentTypes, setFilteredContentTypes] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && strategy.description) {
      // Extract a short description from the strategy description
      const shortDesc = strategy.description.slice(0, 297);
      setFormData(prev => ({
        ...prev,
        shortDescription: shortDesc.length > 297 ? shortDesc + '...' : shortDesc
      }));
      
      // Filter content types based on idolPlannerItems
      const filterContentTypes = () => {
        try {
          // Get idol planner items from localStorage
          const savedItems = localStorage.getItem('idolPlannerItems');
          if (!savedItems) {
            setFilteredContentTypes([]);
            return;
          }
          
          const idolItems: IdolItem[] = JSON.parse(savedItems);
          
          // Extract all unique content types from idolItems
          const idolContentTypes = new Set<string>();
          idolItems.forEach(item => {
            if (item.stats && Array.isArray(item.stats)) {
              item.stats.forEach(stat => {
                if (stat.contentType) {
                  idolContentTypes.add(stat.contentType);
                }
              });
            }
          });
          // Filter pinnedContentTypes to only include those present in idolContentTypes
          const filtered = pinnedContentTypes.filter(type => idolContentTypes.has(type));
          setFilteredContentTypes(filtered);
        } catch (error) {
          console.error('Error filtering content types:', error);
          setFilteredContentTypes([]);
        }
      };
      
      filterContentTypes();
    }
  }, [isOpen, strategy.description, pinnedContentTypes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const isValidUrl = (url: string) => {
    if (!url) return true; // Empty is valid (optional field)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate URLs
    if (!isValidUrl(formData.authorLink) || !isValidUrl(formData.strategyLink)) {
      setSubmitStatus({
        success: false,
        message: 'Please enter valid URLs or leave fields empty'
      });
      return;
    }
    
    // Get the share string
    const shareString = ShareStateService.createShareableUrl().split('?s=')[1];
    
    // Construct payload
    const payload = {
      id: strategy.name,
      difficulty: strategy.difficulty,
      cost: strategy.cost,
      content: filteredContentTypes,  // Use filtered content types here
      shortDescription: formData.shortDescription,
      authorLink: formData.authorLink || undefined,
      strategyLink: formData.strategyLink || undefined,
      shareString: shareString
    };
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        onSubmitSuccess('Strategy submitted successfully! It will be reviewed by the website creator.');
        onClose();
      } else {
        setSubmitStatus({
          success: false,
          message: 'Failed to submit strategy. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Error submitting strategy:', error);
      setSubmitStatus({
        success: false,
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Submit Your Strategy" customWidth="max-w-2xl">
      <div className="space-y-4">
        <div className="bg-blue-800 text-blue-100 p-3 rounded text-sm mb-4">
          <p>Share your strategy with the community! After submission, your strategy will be reviewed by the website creator before being added to the featured strategies section.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Non-editable fields */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Strategy Details (from current strategy)</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700 p-3 rounded">
                <label className="block text-sm font-medium mb-1">Name:</label>
                <div className="text-sm text-gray-300">{strategy.name || 'Unnamed Strategy'}</div>
              </div>
              
              <div className="bg-gray-700 p-3 rounded">
                <label className="block text-sm font-medium mb-1">Difficulty:</label>
                <span className={`px-2 py-1 text-xs rounded inline-block ${
                  strategy.difficulty === 'easy' ? 'bg-green-600' :
                  strategy.difficulty === 'medium' ? 'bg-yellow-600' :
                  strategy.difficulty === 'hard' ? 'bg-orange-600' :
                  strategy.difficulty === 'very hard' ? 'bg-red-600' :
                  'bg-gray-600'
                }`}>
                  Difficulty: {strategy.difficulty.charAt(0).toUpperCase() + strategy.difficulty.slice(1)}
                </span>
              </div>
              
              <div className="bg-gray-700 p-3 rounded">
                <label className="block text-sm font-medium mb-1">Cost:</label>
                <span className={`px-2 py-1 text-xs rounded inline-block ${
                  strategy.cost === 'cheap' ? 'bg-green-600' :
                  strategy.cost === 'medium' ? 'bg-yellow-600' :
                  strategy.cost === 'expensive' ? 'bg-red-600' :
                  'bg-gray-600'
                }`}>
                  Cost: {strategy.cost.charAt(0).toUpperCase() + strategy.cost.slice(1)}
                </span>
              </div>
              
              <div className="bg-gray-700 p-3 rounded">
                <label className="block text-sm font-medium mb-1">Content Types:</label>
                <div className="flex flex-wrap gap-1">
                  {filteredContentTypes.length > 0 ? (
                    filteredContentTypes.map(type => (
                      <span key={type} className="bg-blue-900 px-2 py-0.5 text-xs rounded">
                        {type}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">No content types selected. Use Pin button in Idol Modifiers section.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Editable fields */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Additional Information</h3>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Short Description: <span className="text-gray-400">({formData.shortDescription.length}/300)</span>
              </label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                maxLength={300}
                required
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white h-20"
                placeholder="Brief description of your strategy"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Author Link: <span className="text-gray-400">(optional - Twitch, YouTube, etc.)</span>
              </label>
              <input
                type="text"
                name="authorLink"
                value={formData.authorLink}
                onChange={handleChange}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="https://twitch.tv/yourname"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                Strategy Link: <span className="text-gray-400">(optional - Video guide or explanation)</span>
              </label>
              <input
                type="text"
                name="strategyLink"
                value={formData.strategyLink}
                onChange={handleChange}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="https://youtube.com/watch?v=yourvideo"
              />
            </div>
          </div>
          
          {/* Status message */}
          {submitStatus && (
            <div className={`p-3 rounded text-sm ${
              submitStatus.success ? 'bg-green-700 text-green-100' : 'bg-red-700 text-red-100'
            }`}>
              {submitStatus.message}
            </div>
          )}
          
          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 rounded ${
                isSubmitting
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Your Strategy'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SubmitStrategyModal;